import React, { Fragment, memo } from 'react';
import * as PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import cn from 'classnames';

import { targetUserSelector } from '../../../redux/context/selectors';
import * as historicMeasurements from '../../../redux/profiles/patientHealthboard/historic-measurements-req';
import { singleDayGrafModalActions } from '../../../redux/profiles/patientHealthboard/single-day-graf-modal';
import {
  HISTORIC_ONE_DAY_CALENDAR_TYPE,
  VITALS_ACTIVITY_TYPE,
} from '../constants';
import Loader from '../../common/Loader';
import {
  adaptHistoricResponse,
  concatDataHistoricValue,
  getDataListByType,
  getSafeDescriptionDataHistoricList,
} from '../utils';
import styles from './index.styl';
import {
  selectHistoricMeasurementsList,
  selectHistoricMeasurementsListPending,
  selectHistoricMeasurementsPreviousDate,
} from '../../../redux/profiles/patientHealthboard/selectors';
import { useSafeDataSourceTitle } from '../../common/Charts/hooks';

function DataHistoricList({
  formatMessage,
  data,
  type,
  pending,
  userId,
  isWeightInKg,
  isHeightInMeters,
  allValuesForOneDay = false,
  calendarFormatTime,
  mainUSer,
  isMilligramsPerDeciliter,
}) {
  const historicData = adaptHistoricResponse(
    useSelector(selectHistoricMeasurementsList),
  );

  const { getSafeDataSourceTitle } = useSafeDataSourceTitle();

  const previousDate = useSelector(selectHistoricMeasurementsPreviousDate);

  const historicPending = useSelector(selectHistoricMeasurementsListPending);
  const dispatch = useDispatch();
  const targetUserFirstName = get(
    useSelector(targetUserSelector),
    'firstName',
    '',
  );

  const safeData = data || concatDataHistoricValue(historicData, type);
  const safePending = pending || historicPending;

  const safeFormatCalendar =
    calendarFormatTime || HISTORIC_ONE_DAY_CALENDAR_TYPE;

  const isNotHeightData = type !== VITALS_ACTIVITY_TYPE.HEIGHT;

  const handleLoadMoreClick = () => {
    dispatch(
      historicMeasurements.actions.send({
        typeMeasurements: type,
        periodStartDate: previousDate,
        userId,
      }),
    );
  };

  const handleClickCard = date => {
    if (isNotHeightData) {
      dispatch(
        singleDayGrafModalActions.open({
          type,
          baseDate: date,
          dataList: [],
          singleDayMode: true,
        }),
      );
    }
  };

  return (
    <div
      className={cn(styles.root, {
        [styles.rootWrapperForOneDay]: allValuesForOneDay,
      })}
    >
      {!allValuesForOneDay && type && (
        <Fragment>
          <div className={styles.title}>
            {formatMessage({
              id: `patient.healthboard.titleHistoric.${type}`,
            })}
          </div>
          <div className={styles.dataDescription}>
            {getSafeDescriptionDataHistoricList(type, formatMessage)}
          </div>
        </Fragment>
      )}
      {safePending && safeData.length === 0 && <Loader />}
      {safeData.length !== 0 &&
        type &&
        safeData.map(item => {
          const {
            value,
            metric,
            measuredAt,
            rowDate,
            dataSource,
          } = getDataListByType({
            formatMessage,
            type,
            item,
            isWeightInKg,
            isHeightInMeters,
            isMilligramsPerDeciliter,
            allValuesForOneDay,
            safeFormatCalendar,
          });
          return (
            <div
              className={cn(styles.cardWrapper, {
                [styles.heightDataCard]: !isNotHeightData,
                [styles.cardForOneDay]: allValuesForOneDay,
              })}
              key={item.measuredAt}
              onClick={() => handleClickCard(rowDate)}
            >
              <div className={styles.valueWrapper}>
                <div className={styles.value}>{value}</div>
                <div className={styles.metric}>{metric}</div>
              </div>
              <div className={styles.rightSideWrapper}>
                <div className={styles.date}>{measuredAt}</div>
                <div className={styles.dataSource}>
                  {getSafeDataSourceTitle(
                    dataSource,
                    formatMessage,
                    mainUSer,
                    targetUserFirstName,
                  )}
                </div>
              </div>
            </div>
          );
        })}
      {safePending && safeData.length !== 0 && <Loader />}
      {!allValuesForOneDay && (
        <div
          className={cn(styles.loadMore, {
            [styles.disableLoadMore]: safePending,
          })}
          onClick={() => handleLoadMoreClick()}
        >
          {formatMessage({ id: 'patient.healthboard.list.loadMore' })}
        </div>
      )}
    </div>
  );
}

DataHistoricList.propTypes = {
  formatMessage: PropTypes.func,
  data: PropTypes.array,
  type: PropTypes.string,
  pending: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isWeightInKg: PropTypes.bool,
  isMilligramsPerDeciliter: PropTypes.bool,
  isHeightInMeters: PropTypes.bool,
  allValuesForOneDay: PropTypes.bool,
  calendarFormatTime: PropTypes.object,
  mainUSer: PropTypes.bool,
};

export default memo(DataHistoricList);



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/DataHistoricList/index.jsx